import Text from 'components/_UI/Text';
import { useCheckout } from 'context/checkout/Checkout';
import currency from 'helpers/currency';
import React, { useState, useEffect } from 'react';
import { Minus, MinusCircle, MinusSquare, Plus, PlusCircle, PlusSquare } from 'lucide-react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import date from 'helpers/date';
import serviceFee from 'helpers/serviceFee';
import { trackEvent } from 'scripts/pixels';
import { FullyCentered } from 'styled/alignment/Center';
import fonts from 'theme/fonts';

export default function TicketItem({ ticket, type, soldout, finished, upcoming, saleType, minimal = false, loginModalCount = false, selectedTickets, description, setSelectedTickets, couponTrigger, onChangeTotalValue, absorbedServiceFee, requiresDocument, maxPerOrder = null, productInCart = 0, purchaseLocked = false }) {
    const [quantity, setQuantity] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);

    // Limite do produtor para o ingresso, somando os lotes dele no carrinho
    // (`productInCart`); o de 10 por lote continua valendo junto.
    const productLimitReached = !!maxPerOrder && productInCart >= maxPerOrder;
    const canIncrement = quantity < ticket.quantity && quantity < 10 && !productLimitReached && !purchaseLocked;

    const { checkout, setCheckout } = useCheckout();

    // A taxa pode ser diferente entre cartão e pix desde que o evento passou a
    // ter taxa negociável. A vitrine mostra a de cartão; a de pix segue junto
    // para o total ser recalculado quando o cliente escolher o meio.
    const feePerUnit = serviceFee.perUnit(ticket, serviceFee.METHODS.CREDIT_CARD, absorbedServiceFee);
    const pixFeePerUnit = serviceFee.perUnit(ticket, serviceFee.METHODS.PIX, absorbedServiceFee);

    // Função para calcular a quantidade de itens selecionados por id
    const calculateQuantityFromSelectedTickets = (id) => {
        return selectedTickets.filter(selectedTicket => selectedTicket.id === id).length;
    };

    const incrementQuantity = () => {
        if (canIncrement) {
            setQuantity(prev => prev + 1);

            const updatedTotalPrice = ticket.price * (quantity + 1);
            const updatedTotalOriginalPrice = Number(ticket.original_price) * (quantity + 1);
            const priceChange = updatedTotalPrice - totalPrice;
            const totalPriceChange = updatedTotalOriginalPrice - (Number(ticket.original_price) * quantity);

            setTotalPrice(updatedTotalPrice);
            onChangeTotalValue(priceChange, totalPriceChange, feePerUnit, pixFeePerUnit);

            // Adiciona uma nova instância do ticket completo ao selectedTickets
            setSelectedTickets(prevSelectedTickets => [
                ...prevSelectedTickets,
                {
                    ...ticket,
                    type,
                    absorbed_service_fee: absorbedServiceFee,
                    requires_document: requiresDocument
                }
            ]);

            // Uma unidade por clique: o valor do evento é o do ingresso somado,
            // não o carrinho inteiro, que já vai no InitiateCheckout.
            trackEvent('AddToCart', {
                value: ticket.price,
                contentIds: [ticket.id],
                numItems: 1,
            });
        }
    };

    const decrementQuantity = () => {
        if (quantity > 0) {
            setQuantity(prev => prev - 1);

            const updatedTotalPrice = ticket.price * (quantity - 1);
            const updatedTotalOriginalPrice = Number(ticket.original_price) * (quantity - 1);
            const priceChange = updatedTotalPrice - totalPrice;
            const totalPriceChange = updatedTotalOriginalPrice - (Number(ticket.original_price) * quantity);

            setTotalPrice(updatedTotalPrice);
            onChangeTotalValue(priceChange, totalPriceChange, -feePerUnit, -pixFeePerUnit);

            // Remove apenas uma instância do ticket correspondente
            setSelectedTickets(prevSelectedTickets => {
                const ticketIndex = prevSelectedTickets.findIndex(selectedTicket => selectedTicket.id === ticket.id);
                if (ticketIndex > -1) {
                    const updatedTickets = [...prevSelectedTickets];
                    updatedTickets.splice(ticketIndex, 1); // Remove uma instância do ingresso
                    return updatedTickets;
                }
                return prevSelectedTickets;
            });
        }
    };

    useEffect(() => {
        if (loginModalCount > 1) {
            setQuantity(0);
            setTotalPrice(0);
        }

        if (quantity === 0) {
            setCheckout(prevCheckout => ({
                ...prevCheckout,
                batches: prevCheckout?.batches?.filter(item => item.id !== ticket.id) || []
            }));
        } else {
            setCheckout(prevCheckout => {
                const existingBatch = prevCheckout?.batches?.find(item => item.id === ticket.id);
                if (existingBatch) {
                    // Atualiza a quantidade do item existente
                    return {
                        ...prevCheckout,
                        batches: prevCheckout.batches.map(item =>
                            item.id === ticket.id
                                ? { ...item, quantity: quantity }
                                : item
                        )
                    };
                } else {
                    return {
                        ...prevCheckout,
                        batches: [...prevCheckout.batches, { id: ticket.id, quantity: quantity }]
                    };
                }
            });
        }
    }, [quantity, loginModalCount]);

    // useEffect para lidar com o couponTrigger
    useEffect(() => {
        // Atualiza a quantidade conforme o selectedTickets
        const newQuantity = calculateQuantityFromSelectedTickets(ticket.id);
        setQuantity(newQuantity);

        const updatedTotalPrice = ticket.price * newQuantity;
        const updatedTotalOriginalPrice = Number(ticket.original_price) * newQuantity;
        const priceChange = updatedTotalPrice - totalPrice;
        const totalPriceChange = updatedTotalOriginalPrice - totalPrice;
        const quantityChange = newQuantity - quantity;
        const feeChange = feePerUnit * quantityChange;
        const pixFeeChange = pixFeePerUnit * quantityChange;

        setTotalPrice(updatedTotalPrice);
        onChangeTotalValue(priceChange, totalPriceChange, feeChange, pixFeeChange);
    }, [couponTrigger, ticket.price, ticket.original_price]);

    return (
        <>
            {
                (!!ticket?.is_active && ticket?.quantity > 0) &&(
                    <TicketItemContainer minimal={minimal} >
                        {/* min-width: 0 é o que deixa este flex item encolher
                            abaixo do conteúdo (uma descrição comprida, por
                            exemplo) em vez de estourar e empurrar o seletor
                            de quantidade pra fora do card. */}
                        <Column style={{ minWidth: 0 }}>
                            <Text type='bold' marginBottom={'5px'} color={colors.nightDark}>
                                {ticket.name}
                            </Text>

                            {
                                (ticket?.description || description) && (
                                    <ContentTextDescription>
                                        <Text
                                            color={colors.night}
                                            marginBottom={'5px'}
                                            marginTop={'-6px'}
                                            name='small'
                                            paddingRight={'25px'}
                                        >
                                            {ticket?.description || description}
                                        </Text>
                                    </ContentTextDescription>
                                )
                            }

                            <Column width='80%'>
                                {
                                    Number(ticket?.original_price) === Number(ticket?.price) &&
                                    <Row marginBottom={'5px'} verticalCenter>
                                        <Text type='bold' color={colors.night} paddingBottom={0}>
                                            {ticket?.original_price != 0 ? currency.format(ticket?.original_price) : 'Gratuito'}
                                        </Text>
                                        {
                                            feePerUnit > 0 &&
                                            <Text name='small' color={colors.night} marginLeft={'4px'} paddingBottom={0}>
                                                (+{feePerUnit.toFixed(2).replace('.', ',')} taxa)
                                            </Text>
                                        }
                                    </Row>
                                }
                                {
                                    Number(ticket?.original_price) !== Number(ticket?.price) &&
                                    <Row marginBottom={'5px'} verticalCenter>
                                        <Text
                                            color={colors.nightLight}
                                            marginRight={'.5rem'}
                                            decoration={'line-through'}
                                        >
                                            {currency.format(ticket?.original_price)}
                                        </Text>
                                        <Text type='bold' color={colors.success} paddingBottom={0}>
                                            {currency.format(ticket?.price)}
                                        </Text>
                                        {
                                            feePerUnit > 0 &&
                                            <Text name='small' color={colors.night} marginLeft={'4px'} paddingBottom={0}>
                                                (+{feePerUnit.toFixed(2).replace('.', ',')} taxa)
                                            </Text>
                                        }
                                    </Row>
                                }

                                {
                                    (ticket.sale_end && saleType == 'sale_period') &&
                                    <Text color={colors.night} name='small' type='italic' paddingBottom={'4px'}>
                                        Vendas até {date.format(ticket.sale_end)}
                                    </Text>

                                }

                                {
                                    !!maxPerOrder &&
                                    <Text color={colors.night} name='small' type='italic' paddingBottom={'4px'}>
                                        Até {maxPerOrder} por compra
                                    </Text>
                                }

                                {
                                    (ticket.quantity <= 15 && !soldout && !finished) &&
                                    <LastUnits>
                                        <Text
                                            color={colors.occasionalOrange}
                                            name='small'
                                            paddingBottom={0}
                                        >
                                            Restam poucos ingressos
                                        </Text>
                                    </LastUnits>
                                }
                            </Column>
                        </Column>

                        <QuantityContainer>
                            {
                                !soldout && (
                                    <>
                                        <ToggleValuesContainer fullyCentralized>
                                            <ActionContainer
                                                minus
                                                disabled={quantity === 0}
                                                onClick={decrementQuantity}
                                            >
                                                <Minus
                                                    style={{ cursor: `${quantity === 0 ? 'not-allowed' : 'pointer'}` }}
                                                    color={quantity === 0 ? colors.night : colors.neutralLight}
                                                    size={20}
                                                />
                                            </ActionContainer>

                                            <Text marginLeft={'6px'} marginRight={'6px'} color={colors.night} paddingBottom={0}>
                                                {quantity}
                                            </Text>

                                            <ActionContainer
                                                add
                                                disabled={!canIncrement}
                                                onClick={incrementQuantity}
                                            >
                                                <Plus
                                                    color={!canIncrement ? colors.nightLight : colors.neutralLight}
                                                    size={20}
                                                />
                                            </ActionContainer>
                                        </ToggleValuesContainer>
                                        <Text type='bold' color={colors.night} paddingBottom={0}>
                                            R$ {(totalPrice + feePerUnit * quantity).toFixed(2)}
                                        </Text>
                                    </>
                                )
                            }

                        </QuantityContainer>
                    </TicketItemContainer >
                )
            }

            {
                (soldout || finished || upcoming) && (
                    <TicketItemContainer minimal={minimal} >
                        <Column style={{ minWidth: 0 }}>
                            <Text type='bold' marginBottom={'5px'} color={colors.nightDark}>
                                {ticket.name}
                            </Text>

                            {
                                (ticket?.description || description) && (
                                    <ContentTextDescription>
                                        <Text
                                            color={colors.night}
                                            marginBottom={'5px'}
                                            marginTop={'-6px'}
                                            name='small'
                                            paddingRight={'25px'}
                                        >
                                            {ticket?.description || description}
                                        </Text>
                                    </ContentTextDescription>
                                )
                            }

                            <Column width='80%'>
                                {
                                    !upcoming && Number(ticket?.original_price) === Number(ticket?.price) &&
                                    <Text color={colors.night} marginBottom={'5px'}>
                                        {ticket?.original_price != 0 ? currency.format(ticket?.original_price) : 'Gratuito'}
                                    </Text>
                                }
                                {
                                    !upcoming && Number(ticket?.original_price) !== Number(ticket?.price) &&
                                    <Row marginBottom={'5px'}>
                                        <Text
                                            color={colors.nightLight}
                                            marginRight={'.5rem'}
                                            decoration={'line-through'}
                                        >
                                            {currency.format(ticket?.original_price)}
                                        </Text>
                                        <Text color={colors.success}>
                                            {currency.format(ticket?.price)}
                                        </Text>
                                    </Row>
                                }

                                {
                                    (upcoming && ticket?.sale_start) &&
                                    <Text color={colors.night} name='small' type='italic' paddingBottom={'4px'}>
                                        Vendas a partir de {date.formatDateTime(ticket.sale_start)}
                                    </Text>
                                }
                            </Column>
                        </Column>

                        <QuantityContainer>
                            {
                                upcoming && (
                                    <Text marginLeft={'6px'} marginRight={'6px'} color={colors.occasionalPurple} paddingBottom={0} type='bold'>
                                        Em breve
                                    </Text>
                                )
                            }

                            {
                                !upcoming && (soldout || ticket?.quantity == 0) && (
                                    <Text marginLeft={'6px'} marginRight={'6px'} color={colors.night} paddingBottom={0} type='bold'>
                                        Esgotado
                                    </Text>
                                )
                            }

                            {
                                !upcoming && !soldout && finished && (
                                    <Text marginLeft={'6px'} marginRight={'6px'} color={colors.dangerLight} paddingBottom={0} type='bold'>
                                        Encerrado
                                    </Text>
                                )
                            }

                        </QuantityContainer>
                    </TicketItemContainer >
                )
            }
        </>
    )
    // : (
    //     <Column marginTop='15px'>
    //         <Text textAlign={'center'} color={colors.night} type='bold' name={fonts.sizes.default}>
    //             Ingressos pausados pelo produtor.
    //         </Text>
    //     </Column>
    // )
}

const TicketItemContainer = styled(Row)`
    margin-top: 20px;
    /* padding: 14px 0; */
    align-items: center;
    /* background-color: ${colors.neutral}; */
    border-radius: 6px;
    margin-top: 14px;
    padding: 14px;
    border: 1px solid ${colors.neutralDark};
    box-shadow: 0 0 3px #ccc;
    /* border-bottom: 1px solid ${colors.nightLight}; */

    /* ${({ stoped }) => stoped && `
        opacity: .65;
    `}; */
    
    /* ${({ soldout }) => soldout && `
        opacity: .7;
    `}; */

    /* @media screen and (min-width: ${breakpoints.lg}) {
        border-bottom: none;
        /* border-top: 1px solid ${colors.nightLight}; */
        /* margin-top: 0; */
        
        /* &:first-child {
            border-top: none;
        } */

        /* ${({ minimal }) => minimal && `
            border-top: none;
            border-bottom: 1px solid ${colors.nightLight};
        `} */
    /* } */ 
`;

const LastUnits = styled(FullyCentered)`
    border: 1px solid ${colors.occasionalOrange};
    border-radius: 30px;
    width: 162px;
    padding: 5px;
    background-color: #fcf7eb;
`;

const QuantityContainer = styled(Column)`
    flex-direction: column;
    width: auto;
    align-items: center;
    width: 100px;
    /* Sem isto, uma descrição sem onde quebrar linha força o irmão a não
       encolher, e o flex empurra a sobra pra cá — os botões e o preço saem
       espremidos/deformados em vez de continuar com 100px. */
    flex-shrink: 0;
`;

const ToggleValuesContainer = styled(Row)`
    margin-bottom: 10px;
    width: auto;
    /* border: 1px solid ${colors.nightLight};
    border-radius: 6px; */
    /* padding: 6px; */
`;

const ActionContainer = styled(FullyCentered)`
    width: 30px;
    height: 30px;
    background: ${colors.occasionalPurple};
    border-radius: 6px;
    cursor: pointer;
    transition: all ease-in-out .25s;

    
    ${({ add }) => add && `
        margin-left: 4px;
        
        &:hover { 
            opacity: .8;
        }
    `}
    
    ${({ minus }) => minus && `
        margin-right: 4px;

        &:hover { 
            opacity: .8;
        }
    `}

    ${({ disabled }) => disabled && `
        background: ${colors.neutralLight};
        border: 1px solid ${colors.night};
        pointer-events: none;
    `}
`;


const ContentTextDescription = styled.div`
    & > p {
        word-break: keep-all;
    }
`
